// ─────────────────────────────────────────────────────────────────────────────
// Journal guide page
//
// The trade log surface — browse every trade you've logged or imported,
// click a row to see the full record, edit if needed.
//
// Sections cover:
//   - account selector + filter tabs (All / Win / Loss)
//   - the list columns (date, symbol, direction, setup, P&L, score)
//   - moon icon (after-hours trades)
//   - clicking a trade → detail modal with all 3 sections of the wizard
//   - Edit Trade flow
//   - demo mode banner
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideJournal() {
  return (
    <GuideLayout
      title="Journal"
      subtitle="Your full trade history. Browse every trade you've logged or imported, view the complete record, and edit any field."
      prev={{ slug: "trade-review", label: "Trade Review" }}
      next={{ slug: "analytics",    label: "Analytics" }}
    >
      <H2First>What it's for</H2First>
      <P>
        The Dashboard shows your recent trades. Trade Review pulls one
        trade and surrounds it with coaching feedback. The Journal is
        where you go to <em>browse</em> — see everything you've logged
        for an account, find a specific trade, and edit anything you
        need to correct.
      </P>
      <P>
        If you imported your history from a CSV, this is where you'll
        spend time backfilling the behavioral fields (mental state,
        setup type, stop loss) that broker exports don't include.
      </P>

      <H2>The trade log</H2>
      <P>
        Below the account selector is a table listing every trade for
        the active account. Columns:
      </P>
      <UL>
        <LI><UI>Date</UI> — trade date and entry time. A 🌙 icon between the date and time means the trade was placed after the daily market close and is grouped into the next session day (see Dashboard guide for details).</LI>
        <LI><UI>Symbol</UI> — instrument traded</LI>
        <LI><UI>Dir</UI> — direction arrow: green up for long, red down for short</LI>
        <LI><UI>Setup</UI> — the setup type pill, if you logged one</LI>
        <LI><UI>Entry</UI> and <UI>Exit</UI> — the trade prices</LI>
        <LI><UI>P&L</UI> — gain or loss for the trade</LI>
        <LI><UI>Score</UI> — the 1–10 discipline score that also shows in Trade Review</LI>
      </UL>

      <ScreenshotFrame
        src="/guide/journal-list.png"
        alt="Journal list view with all trades"
        caption="The Journal table — every trade for the selected account, most recent at the top."
      />

      <H2>Filtering the view</H2>
      <P>
        Above the table, three tabs let you narrow what's shown:
      </P>
      <UL>
        <LI><UI>All Trades</UI> — the full list (default)</LI>
        <LI><UI>Win</UI> — only profitable trades</LI>
        <LI><UI>Loss</UI> — only losing trades</LI>
      </UL>
      <P>
        Use the Win/Loss filters when you want to study one side of
        your trading in isolation — e.g. "what setup types am I taking
        on my losers" or "what does my exit reason look like across my
        winners."
      </P>

      <Callout kind="info" title="Switching accounts">
        The Journal shows trades for one account at a time. Use the
        <UI>Viewing Account</UI> selector at the top to switch between
        your combine, your funded account, your sim, etc. Each one has
        its own ledger.
      </Callout>

      <H2>Clicking a trade — the detail modal</H2>
      <P>
        Click any row in the list and a modal opens showing the full
        record of that trade, organized into the same three sections
        you filled in at Trade Entry:
      </P>

      <ScreenshotFrame
        src="/guide/journal-detail-modal.png"
        alt="Trade detail modal opened from the Journal list"
        caption="The detail modal shows every field you logged, grouped by stage."
      />

      <UL>
        <LI><strong>Pre-Trade</strong> — market condition, entry signal, conviction, focus, rule adherence, impulsiveness, energy, market context, outcome satisfaction, mental state</LI>
        <LI><strong>Execution</strong> — date, times, prices, stop loss, take profit, position size, setup type, duration</LI>
        <LI><strong>Post-Trade Reflection</strong> — exit reason, fear and greed levels, post-trade emotion, outcome satisfaction, and your two free-text reflection answers</LI>
      </UL>

      <P>
        Fields you skipped at Trade Entry simply don't appear in the
        modal — there's no "—" or "N/A" clutter for missing data. What
        you see is everything you logged for this trade.
      </P>

      <H2>Editing a trade</H2>
      <P>
        At the bottom of the detail modal is an <UI>✏️ Edit Trade</UI>{" "}
        button. Clicking it lets you update any field on the trade —
        prices, times, behavioral fields, reflection notes, anything.
      </P>
      <P>
        Two common reasons to edit:
      </P>
      <UL>
        <LI><strong>Backfilling imported trades.</strong> CSV imports bring in the trade mechanics but not behavioral fields. Open an imported trade and add the setup type, mental state, stop loss, etc. — the analytics suites will start including it in their detector reads.</LI>
        <LI><strong>Fixing typos or missing data.</strong> Wrong contract count, missed commissions, exit time off by an hour — open the trade, fix the field, save.</LI>
      </UL>
      <P>
        Edits flow through everywhere. The Dashboard's recent trades
        list, the Analytics suites, Trade Review's coaching notes — all
        of it recomputes based on the updated trade. There's no
        separate "re-analyze" step.
      </P>

      <Callout kind="tip">
        Backfilling 5–10 imported trades a day with their behavioral
        fields is the fastest way to get the Psychology and Context
        analytics tabs firing. Pick a recent trade you remember well,
        open it from the Journal, fill in mental state and exit reason
        from memory, save. Done.
      </Callout>

      <H2>Demo mode</H2>
      <P>
        If you're using a demo account or your trial hasn't activated,
        you'll see a "Demo mode — trade data is read-only. Sign up to
        log and manage your own trades" banner at the top of the
        Journal. The trade list still browses, but the Edit Trade
        button is disabled.
      </P>

      <H2>How the Journal fits with everything else</H2>
      <P>
        The Journal is the inventory view. The other surfaces use it as
        the source of truth:
      </P>
      <UL>
        <LI><UI>Dashboard</UI> shows the most recent 5 trades from your Journal</LI>
        <LI><UI>Trade Review</UI> pulls the last 20 trades from your Journal to pick from</LI>
        <LI><UI>Analytics</UI> runs its detectors across your full Journal history</LI>
        <LI><UI>Eval Survival</UI> replays your Journal against prop firm rules to test pass rates</LI>
      </UL>
      <P>
        If something looks wrong in any of those places, the Journal is
        where you fix it — edit the underlying trade and the views
        update automatically.
      </P>
    </GuideLayout>
  );
}
